import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import {
  FontTypes,
  injectCSSVariablesIntoDOM,
  LobbySectionType,
  MultiWheel,
  Quiz,
  Spinmachine,
  Transaction,
  Wheel,
  emitIframeMessage,
} from '@longnecktech/splash-commons-fe';
import { IframeMessage } from '@longnecktech/splash-commons-fe/dist/types/iframe-message';
import { SessionService } from '@services/session.service';
import { DefaultGameID, HubGame } from '@shared/types/game';
import { LobbyGameType } from '@shared/types/game-group';
import { NextSpinInfo } from '@shared/types/spin-info';
import { Theme } from '@shared/types/theme';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  pipe,
  take,
  tap,
} from 'rxjs';

interface GameObservables {
  quizGames?: Observable<Quiz>[];
  wheelGames?: Observable<Wheel>[];
  spinmachineGames?: Observable<Spinmachine>[];
  multiWheelGames?: Observable<MultiWheel>[];
}

@Injectable({
  providedIn: 'root',
})
export class GamesService {
  private http = inject(HttpClient);
  private session = inject(SessionService);

  private _isLoading = new BehaviorSubject<boolean>(false);
  private _quizGames = new BehaviorSubject<Quiz[]>([]);
  private _spinmachineGames = new BehaviorSubject<Spinmachine[]>([]);
  private _wheelGames = new BehaviorSubject<Wheel[]>([]);
  private _multiWheelGames = new BehaviorSubject<MultiWheel[]>([]);
  private _quizTransactions = new BehaviorSubject<
    Record<string, Transaction | null>
  >({});
  private _nextSpinInfo = new BehaviorSubject<
    Record<string, NextSpinInfo | undefined>
  >({});
  private _sportGamesCount = new BehaviorSubject<number>(0);
  private _wheelGamesCount = new BehaviorSubject<number>(0);
  private _spinmachineGamesCount = new BehaviorSubject<number>(0);
  private _multiWheelGamesCount = new BehaviorSubject<number>(0);

  readonly quizGames$ = this._quizGames.asObservable();
  readonly spinmachineGames$ = this._spinmachineGames.asObservable();
  readonly wheelGames$ = this._wheelGames.asObservable();
  readonly multiWheelGames$ = this._multiWheelGames.asObservable();
  readonly quizTransactions$ = this._quizTransactions.asObservable();
  readonly nextSpinInfo$ = this._nextSpinInfo.asObservable();
  readonly sportGamesCount$ = this._sportGamesCount.asObservable();
  readonly wheelGamesCount$ = this._wheelGamesCount.asObservable();
  readonly spinmachineGamesCount$ = this._spinmachineGamesCount.asObservable();
  readonly multiWheelGamesCount$ = this._multiWheelGamesCount.asObservable();

  fetchGame() {
    this._isLoading.next(true);

    return this.http
      .get<HubGame>(
        `${environment.backendUrl}/api/hub/game?${this.getQueryParams()}`,
      )
      .pipe(
        tap((data: HubGame) => {
          this.session.setCurrentGame(data);
          this.session.updateAuthToken();
          this.removeGlobalLoader();
          if (data.theme) {
            this.setCssVariables(data.theme);
          }
          emitIframeMessage(IframeMessage.GameLoaded);
        }),
        mergeMap((data: HubGame) => {
          if (
            data.sections.includes(LobbySectionType.GAMES) &&
            data.activeGames.length
          ) {
            return this.fetchGames(data);
          }
          return of(null);
        }),
        finalize(() => this._isLoading.next(false)),
      );
  }

  fetchGames(hub: HubGame): Observable<{
    quizGames?: Quiz[];
    wheelGames?: Wheel[];
    spinmachineGames?: Spinmachine[];
    multiWheelGames?: MultiWheel[];
  }> {
    const obs = this.getGamesRequests(hub);
    return forkJoin({
      ...(obs.quizGames ? { quizGames: forkJoin(obs.quizGames) } : undefined),
      ...(obs.wheelGames
        ? { wheelGames: forkJoin(obs.wheelGames) }
        : undefined),
      ...(obs.spinmachineGames
        ? { spinmachineGames: forkJoin(obs.spinmachineGames) }
        : undefined),
      ...(obs.multiWheelGames
        ? { multiWheelGames: forkJoin(obs.multiWheelGames) }
        : undefined),
    }).pipe(
      // there could be NULL result in case of error or if there is no a game with such instance
      map(({ quizGames, wheelGames, spinmachineGames, multiWheelGames }) => ({
        quizGames: quizGames ? quizGames?.filter((g) => !!g) : [],
        wheelGames: wheelGames ? wheelGames?.filter((g) => !!g) : [],
        spinmachineGames: spinmachineGames
          ? spinmachineGames?.filter((g) => !!g)
          : [],
        multiWheelGames: multiWheelGames
          ? multiWheelGames?.filter((g) => !!g)
          : [],
      })),
      tap(({ quizGames, wheelGames, spinmachineGames, multiWheelGames }) => {
        this._quizGames.next(quizGames);
        this._wheelGames.next(wheelGames);
        this._spinmachineGames.next(spinmachineGames);
        this._multiWheelGames.next(multiWheelGames);
      }),
    );
  }

  finishLoading(id: string) {
    return pipe(
      take(1),
      catchError((error) => {
        console.error(`Error fetching game ${id}:`, error);
        return of(null);
      }),
    );
  }

  fetchQuiz(instance?: string): Observable<Quiz> {
    const params = instance ? `instance=${instance}` : '';
    return this.http
      .get<Quiz>(`${environment.backendUrl}/api/quiz/game?${params}`)
      .pipe(
        tap((game) => {
          if (!game) {
            // don't show loader for this game anymore
            this._sportGamesCount.next(this._sportGamesCount.value - 1);
          }
        }),
      );
  }

  fetchWheel(instance?: string): Observable<Wheel> {
    const params = instance ? `instance=${instance}` : '';
    return this.http
      .get<Wheel>(`${environment.backendUrl}/api/wheel/game?${params}`)
      .pipe(
        tap((game) => {
          if (!game) {
            // don't show loader for this game anymore
            this._wheelGamesCount.next(this._wheelGamesCount.value - 1);
          }
        }),
      );
  }

  fetchMultiWheel(instance?: string): Observable<MultiWheel> {
    const params = instance ? `instance=${instance}` : '';
    return this.http
      .get<MultiWheel>(
        `${environment.backendUrl}/api/multi-wheel/game?${params}`,
      )
      .pipe(
        tap((game) => {
          if (!game) {
            this._multiWheelGamesCount.next(
              this._multiWheelGamesCount.value - 1,
            );
          }
        }),
      );
  }

  fetchSpinmachine(instance?: string): Observable<Spinmachine> {
    const params = instance ? `instance=${instance}` : '';
    return this.http
      .get<Spinmachine>(
        `${environment.backendUrl}/api/spinmachine/game?${params}`,
      )
      .pipe(
        tap((game) => {
          if (!game) {
            // don't show loader for this game anymore
            this._spinmachineGamesCount.next(
              this._spinmachineGamesCount.value - 1,
            );
          }
        }),
      );
  }

  getQuizTransaction(gameUuid: string) {
    return this.http
      .get<Transaction | null>(
        environment.backendUrl +
          `/api/quiz/game/${gameUuid}/transaction?withGame=false`,
      )
      .pipe(
        tap((transaction) =>
          this._quizTransactions.next({
            ...this._quizTransactions.value,
            // not undefined, so that we could check this in the component
            [gameUuid]: transaction || null,
          }),
        ),
      );
  }

  getWheelInfo(gameUuid: string): Observable<NextSpinInfo> {
    return this.http
      .get<NextSpinInfo>(environment.backendUrl + '/api/wheel/spin/' + gameUuid)
      .pipe(
        tap((data) => {
          this._nextSpinInfo.next({
            ...this._nextSpinInfo.value,
            [gameUuid]: data,
          });
        }),
      );
  }

  getSpinmachineInfo(gameUuid: string): Observable<NextSpinInfo> {
    return this.http
      .get<NextSpinInfo>(
        environment.backendUrl + '/api/spinmachine/spin/' + gameUuid,
      )
      .pipe(
        tap((data) => {
          this._nextSpinInfo.next({
            ...this._nextSpinInfo.value,
            [gameUuid]: data,
          });
        }),
      );
  }

  private getGamesRequests(hub: HubGame): GameObservables {
    const quizInstances = hub.quizInstances.length
      ? hub.quizInstances
      : [DefaultGameID.QUIZ];
    const wheelInstances = hub.wheelInstances.length
      ? hub.wheelInstances
      : [DefaultGameID.WHEEL];
    const spinMachineInstances = hub.spinMachineInstances.length
      ? hub.spinMachineInstances
      : [DefaultGameID.SPINMACHINE];
    // get games' instances only for Active Groups
    return hub.activeGames.reduce((acc: GameObservables, activeGame) => {
      if (activeGame === LobbyGameType.QUIZ) {
        this._sportGamesCount.next(quizInstances.length);
        acc['quizGames'] = quizInstances.map(
          (instance) =>
            this.fetchQuiz(
              instance === DefaultGameID.QUIZ ? undefined : instance,
            ).pipe(this.finishLoading(instance)) as Observable<Quiz>,
        );
      }
      if (activeGame === LobbyGameType.WHEEL) {
        this._wheelGamesCount.next(wheelInstances.length);
        acc['wheelGames'] = wheelInstances.map(
          (instance) =>
            this.fetchWheel(
              instance === DefaultGameID.WHEEL ? undefined : instance,
            ).pipe(this.finishLoading(instance)) as Observable<Wheel>,
        );
      }
      if (activeGame === LobbyGameType.SPIN_MACHINE) {
        this._spinmachineGamesCount.next(spinMachineInstances.length);
        acc['spinmachineGames'] = spinMachineInstances.map(
          (instance) =>
            this.fetchSpinmachine(
              instance === DefaultGameID.SPINMACHINE ? undefined : instance,
            ).pipe(this.finishLoading(instance)) as Observable<Spinmachine>,
        );
      }

      if (activeGame === LobbyGameType.MULTI_WHEEL) {
        const multiWheelInstances = hub.multiWheelInstances?.length
          ? hub.multiWheelInstances
          : [DefaultGameID.MULTIWHEEL];
        this._multiWheelGamesCount.next(multiWheelInstances.length);
        acc['multiWheelGames'] = multiWheelInstances.map(
          (instance) =>
            this.fetchMultiWheel(
              instance === DefaultGameID.MULTIWHEEL ? undefined : instance,
            ).pipe(this.finishLoading(instance)) as Observable<MultiWheel>,
        );
      }
      return acc;
    }, {} as GameObservables);
  }

  private setCssVariables(theme: Theme) {
    const styleEl = document.createElement('style');
    const rootVariables = `:root {
      --block-border-radius: ${theme.blocksBorderRadius ?? 24}px;
      --cards-overlay-opacity: ${(theme.cardsOverlayOpacity ?? 75) / 100};
    }`;
    const textContent = injectCSSVariablesIntoDOM(theme, {
      defaultSecondaryCtaButtonColors: {
        '--z-cta-buttons-outline-background-color': 1,
        '--color-accent-1': 2,
      },
      defaultFonts: {
        [FontTypes.Regular]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Regular.ttf',
        [FontTypes.Bold]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Bold.ttf',
        [FontTypes.Medium]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Medium.ttf',
        [FontTypes.Headlines]:
          '/assets/fonts/Roboto_Condensed/RobotoCondensed-Regular.ttf',
      },
      defaultCtaBorderRadius: 24,
    });
    styleEl.textContent = textContent.concat(rootVariables);
    document.head.appendChild(styleEl);
  }

  private getQueryParams(): string {
    const params: { instance?: string; gameUuid?: string } = {};
    if (this.session.instance) params['instance'] = this.session.instance;
    if (this.session.gameUuid) params['gameUuid'] = this.session.gameUuid;
    return new URLSearchParams(params).toString();
  }

  private removeGlobalLoader(): void {
    if (typeof document === 'undefined') return;
    const el = document.getElementById('global-loader');
    if (el) {
      el.remove();
    }
  }
}
